<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import instagramClient from 'dashboard/api/channel/instagramClient';

import Button from 'next/button/Button.vue';
import ViewWrapper from './ViewWrapper.vue';

const { t } = useI18n();
const router = useRouter();

const inboxesList = useMapGetter('inboxes/getInboxes');

const isConnected = computed(() =>
  inboxesList.value.some(inbox => inbox.channel_type === INBOX_TYPES.INSTAGRAM)
);

const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');
const isRequestingAuthorization = ref(false);

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get('code');
  const errorMessage = urlParams.get('error_message');

  if (errorMessage) {
    hasError.value = true;
    if (errorCode === '400') {
      errorStateMessage.value = errorMessage;
      errorStateDescription.value = t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_AUTH');
    } else {
      errorStateMessage.value = t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_MESSAGE');
      errorStateDescription.value = errorMessage;
    }
  }
  window.history.replaceState({}, document.title, window.location.pathname);
});

const requestAuthorization = async () => {
  isRequestingAuthorization.value = true;
  const response = await instagramClient.generateAuthorization();
  const {
    data: { url },
  } = response;
  window.location.href = url;
};
</script>

<template>
  <ViewWrapper>
    <div class="flex flex-col gap-6">
      <div v-if="hasError" class="py-8">
        <h5 class="text-n-slate-12 font-medium">{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
          class="mt-2 text-sm text-n-slate-11"
        />
      </div>

      <div v-else class="flex flex-col items-start py-10">
        <Button
          class="text-white !rounded-full !px-6 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45]"
          lg
          icon="i-ri-instagram-line"
          :disabled="isRequestingAuthorization"
          :is-loading="isRequestingAuthorization"
          :label="$t('WGPT_ONBOARDING.CHANNELS.INSTAGRAM.CONTINUE')"
          @click="requestAuthorization"
        />
        <p class="py-6 text-xs">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.HELP') }}
        </p>
      </div>
    </div>

    <template #actions>
      <Button
        :label="t('WGPT_ONBOARDING.CHANNELS.BACK')"
        variant="link"
        color="slate"
        size="sm"
        @click="router.push({ name: 'wgpt_onboarding_channels' })"
      />
      <Button
        lg
        :label="t('WGPT_ONBOARDING.CHANNELS.NEXT')"
        :disabled="!isConnected"
        @click="router.push({ name: 'wgpt_onboarding_channels' })"
      />
    </template>
  </ViewWrapper>
</template>
